import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { REFETCH_ORG_NETWORKS } from '~/constants/refetch-intervals'
import { useActiveOrg } from '~/hooks/org/useActiveOrg'
import { useServerRequests } from '~/hooks/useRequests'

const queryKey = {
  setupNetworks: (activeOrgId: string | undefined) => [
    'setupNetworks',
    activeOrgId,
  ],
}

type SetupNetworks = {
  networks: number[]
}

export const useSetupNetworks = () => {
  const { org } = useActiveOrg()

  const { serverGetRequest } = useServerRequests()

  const { data, isLoading } = useQuery<SetupNetworks>({
    queryKey: queryKey.setupNetworks(org?.id),
    queryFn: async () => {
      if (!org) throw new Error()

      const result = await serverGetRequest<SetupNetworks>({
        path: `/api/org/smart_accounts_setup/${org.id}/networks`,
      })

      if (!result?.data) throw new Error()

      return {
        networks: result.data.networks,
      }
    },
    enabled: !!org,
    refetchInterval: REFETCH_ORG_NETWORKS, // The refetch interval is low because two or more users can be on the same org and update the networks at the same time
  })

  return { networks: data?.networks, isLoading }
}

export const useUpdateSetupNetworks = () => {
  const { serverPutRequest } = useServerRequests()
  const queryClient = useQueryClient()

  const { mutate: updateSetupNetworks, isPending } = useMutation({
    mutationFn: async (data: { orgId: string; networks: number[] }) => {
      const result = await serverPutRequest<SetupNetworks>({
        path: `/api/org/smart_accounts_setup/${data.orgId}/networks`,
        params: { networks: data.networks },
        errorMessage: 'Failed to update networks',
      })

      if (!result?.data) throw new Error('Failed to update networks')

      return {
        networks: result.data.networks,
      }
    },
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<SetupNetworks>(
        {
          queryKey: queryKey.setupNetworks(variables.orgId),
        },
        () => {
          return { networks: data.networks }
        },
      )
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKey.setupNetworks(variables.orgId),
      })
    },
  })

  return { updateSetupNetworks, isPending }
}
